import { cloneDeep, debounce } from "lodash";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { FaFolderClosed, FaRegFolderOpen } from "react-icons/fa6";
import { v4 as uuidv4 } from "uuid";
import { organizationEvent$ } from "../../../api/eventBus";
import { eventBusType } from "../../../config/Constant";
import { useAuthLayoutContext } from "../../../context/AuthLayoutContext";
import {
  getChildren,
  searchOrganization,
} from "../../../utils/OrganizationHelper";
import { showName } from "../../../utils/Utils";

const DirectoryContext = createContext(null);

export const useDirectoryContext = () => {
  return useContext(DirectoryContext);
};

export const DirectoryProvider = ({
  children,
  orgList,
  setOrgList,
  isOpen,
  isNonFetch,
  existedMemberIds,
}) => {
  const { orgList: orgs } = useAuthLayoutContext();

  const [tree, setTree] = useState([]);
  const [isCheckedList, setIsCheckedList] = useState(false);
  const [searchResult, setSearchResult] = useState();
  const [isTreeLoading, setIsTreeLoading] = useState(false);
  const [isCheckedLoading, setIsCheckedLoading] = useState(false);
  const [valueName, setValueName] = useState("");

  const getUniqueItems = (itemList) => {
    let uniqueItemIdsSet = new Set(
      itemList
        ?.filter((item) => item?.isOrg === false && item?.checked)
        .map((item) => item.objectId),
    );

    let uniqueItems = Array.from(uniqueItemIdsSet, (itemId) => {
      let correspondingItem = itemList.find((item) => item.objectId === itemId);
      return { itemId, name: correspondingItem?.name };
    });

    return uniqueItems;
  };

  const listToTree = (list) => {
    const map = {};
    const tree = [];

    // Create a mapping of id to object and initialize the tree
    list?.forEach((item) => {
      map[item?.objectId] = { ...item, children: [] };
    });

    // Build the tree by linking children to their parents
    list?.forEach((item) => {
      if (item?.parentId !== null && item?.parentId !== "") {
        const originalItem = list?.find(
          (o) =>
            o?.objectId === item?.objectId && o?.parentId === item?.parentId,
        );

        map[item.parentId]?.children?.push({
          ...map[item?.objectId],
          position: originalItem?.position,
        });
      } else {
        tree.push(map[item?.objectId]);
      }
    });

    return tree;
  };

  const updateCheckedStatus = (data, nodeId, checked) => {
    const getNodesById = (objectId) =>
      data.filter((node) => node?.objectId === objectId);

    const updateChildrenCheckedStatus = (parentId, checked) => {
      const children = data.filter((node) => node.parentId === parentId);
      children.forEach((child) => {
        child.checked = checked;
        updateChildrenCheckedStatus(child?.objectId, checked);
        updateCheckedStatus(data, child?.objectId, checked);
      });
    };

    const updateParentCheckedStatus = (parentId) => {
      const parentNode = data.find((node) => node?.objectId === parentId);
      if (!parentNode) return; // Parent not found

      const allChildrenChecked = data
        .filter((node) => node.parentId === parentId)
        .every((child) => child.checked);

      parentNode.checked = allChildrenChecked;
      parentNode.indeterminate = !allChildrenChecked && parentNode.checked;

      if (parentNode.parentId !== null) {
        updateParentCheckedStatus(parentNode.parentId);
      }
    };

    const updateNodes = (objectId, checked) => {
      getNodesById(objectId).forEach((node) => {
        node.checked = checked;
        updateChildrenCheckedStatus(node?.objectId, checked);
        updateParentCheckedStatus(node.parentId);
      });
    };

    updateNodes(nodeId, checked);
  };

  const updateOnlineStatus = (data, nodeId, online) => {
    const getNodesById = (objectId) =>
      data.filter((node) => node?.objectId === objectId);

    const updateChildrenCheckedStatus = (parentId, online) => {
      const children = data.filter((node) => node.parentId === parentId);
      children.forEach((child) => {
        child.online = online;
        updateChildrenCheckedStatus(child?.objectId, online);
        updateCheckedStatus(data, child?.objectId, online);
      });
    };

    const updateParentCheckedStatus = (parentId) => {
      const parentNode = data.find((node) => node?.objectId === parentId);
      if (!parentNode) return;

      const allChildrenChecked = data
        .filter((node) => node.parentId === parentId)
        .every((child) => child.online);

      parentNode.online = allChildrenChecked;
      parentNode.indeterminate = !allChildrenChecked && parentNode.online;

      if (parentNode.parentId !== null) {
        updateParentCheckedStatus(parentNode.parentId);
      }
    };

    const updateNodes = (objectId, online) => {
      getNodesById(objectId).forEach((node) => {
        node.online = online;
        updateChildrenCheckedStatus(node?.objectId, online);
        updateParentCheckedStatus(node.parentId);
      });
    };

    updateNodes(nodeId, online);
  };

  const fetchOrganizations = async (existedMemberIds) => {
    setIsTreeLoading(true);
    /** prevent to change unmutate */
    setOrgList(cloneDeep(searchOrganization(orgs, null, existedMemberIds)));
    setIsTreeLoading(false);
  };

  const onClickFolder = async (item) => {
    const closeFolder = orgList?.find(
      (org) => org?.objectId === item?.objectId,
    );

    if (closeFolder.isOpen) {
      closeFolder.isOpen = false;
      setOrgList([...orgList]);
    } else {
      closeFolder.isOpen = true;
      setOrgList([...orgList]);
    }
  };

  const openFolder = (item) => {
    if (item?.children?.length === 0) {
      return (
        <>
          <FaFolderClosed
            className="cursor-pointer"
            size={20}
            color="#8C8C8C"
          ></FaFolderClosed>
          <div
            className={
              item?.highlight
                ? "text-[#eda30f] cursor-pointer"
                : "cursor-pointer text-[black]"
            }
          >
            {showName(item?.name)}
          </div>
        </>
      );
    }

    return item?.isOpen ? (
      <>
        <FaRegFolderOpen className="cursor-pointer" size={20} color="#87d068" />
        <div
          className={
            item?.highlight
              ? "text-[#eda30f] cursor-pointer"
              : "cursor-pointer text-[black]"
          }
        >
          {showName(item?.name)}
        </div>
      </>
    ) : (
      <>
        <FaFolderClosed
          className="cursor-pointer"
          size={20}
          color="#539edf"
        ></FaFolderClosed>
        <div
          className={
            item?.highlight
              ? "text-[#eda30f] cursor-pointer w-full"
              : "cursor-pointer text-[black] w-full"
          }
        >
          {showName(item?.name)}
        </div>
      </>
    );
  };

  const onClickCheckBox = (e, item) => {
    e.stopPropagation();
    setIsCheckedLoading(true);
    updateCheckedStatus(orgList, item?.objectId, !item?.checked);
    setOrgList([...orgList]);
    setIsCheckedLoading(false);
  };

  const onDeleteItem = (item) => {
    updateCheckedStatus(orgList, item?.objectId, false);
    setOrgList([...orgList]);
  };

  const showChoseUser = useMemo(() => {
    const users = getUniqueItems(orgList);
    return users?.map((user) => <div key={user?.itemId}>{user?.name}</div>);
  }, [orgList]);

  const debouncedFilterOrg = useMemo(
    () =>
      debounce(async (name) => {
        setIsTreeLoading(true);
        orgList
          ?.filter((org) => org?.parentId)
          ?.forEach((org) => (org.isOpen = false));
        orgList
          ?.filter((org) => org?.highlight)
          ?.forEach((org) => (org.highlight = false));

        if (name !== "") {
          let result = {};
          result = cloneDeep(searchOrganization(orgs, name, existedMemberIds));

          if (result?.length > 0) {
            orgList
              ?.filter((org) => org?.highlight)
              ?.forEach((org) => (org.highlight = false));

            addSearchOrg(result, true);

            const orgPaths = [];
            result?.forEach((org) => {
              if (org?.paths) {
                orgPaths?.push(...org.paths);
              }
            });

            // these is nodes which are belong to searched node parent
            const nearAndRelatedOrgs = getChildren(
              orgs,
              orgPaths?.map((e) => e?.objectId),
              ["3"],
            );

            nearAndRelatedOrgs?.forEach((searchOrg) => {
              const orgIndex = orgList?.findIndex(
                (org) => org?.objectId === searchOrg?.objectId,
              );
              if (orgIndex === -1) {
                orgList?.push({
                  ...searchOrg,
                  checked: false,
                  indeterminate: false,
                  fetched: false,
                  isOpen: false,
                  id: uuidv4(),
                });
              }
            });
          }
        }

        setOrgList([...orgList]);
        setIsTreeLoading(false);
      }, 500),
    [orgList?.length],
  );

  const handleFilterOrg = (name) => {
    setValueName(name);
    debouncedFilterOrg(name);
  };

  const addSearchOrg = (searchOrgs, highlight) => {
    searchOrgs?.forEach((searchOrg) => {
      const org = orgList?.find(
        (org) =>
          org?.objectId === searchOrg?.objectId &&
          org?.parentId === searchOrg?.parentId,
      );

      // no need to open folder if it is highlighted
      if (!org) {
        const orgIds = searchOrg?.paths?.map((e) => e?.objectId);

        const checkedParent = orgList
          ?.filter((e) => orgIds?.includes(e?.objectId))
          ?.find((e) => e?.checked);

        orgList?.push({
          ...searchOrg,
          checked: !!checkedParent,
          indeterminate: false,
          fetched: true,
          isOpen: !highlight,
          id: uuidv4(),
          highlight: highlight,
        });
      } else {
        org.isOpen = !highlight;
        org.highlight = highlight;
      }

      addSearchOrg(searchOrg?.paths, false);
    });
  };

  useEffect(() => {
    const items = listToTree(orgList);
    setTree(items);
  }, [orgList]);

  useEffect(() => {
    if (!isOpen || isNonFetch) return;

    fetchOrganizations(existedMemberIds);
  }, [isOpen, existedMemberIds]);

  useEffect(() => {
    if (typeof setOrgList !== "function") return;

    const sub = organizationEvent$.subscribe((event) => {
      if (event?.type === eventBusType.USER_DETAIL_UPDATED) {
        setOrgList((prev) =>
          prev?.map((element) =>
            element?.userCode === event?.payload?.userCode
              ? { ...element, online: event?.payload?.online }
              : element,
          ),
        );
      }

      if (event?.type === eventBusType.USER_LIST_UPDATED) {
        setOrgList((prev) =>
          prev?.map((element) => {
            const userOrg = event?.payload?.find(
              (u) => u?.userId === element?.userCode,
            );

            return userOrg ? { ...element, online: userOrg?.online } : element;
          }),
        );
      }
    });

    return () => sub.unsubscribe();
  }, []);

  const values = useMemo(
    () => ({
      tree,
      orgList,
      setOrgList,
      getUniqueItems,
      listToTree,
      searchResult,
      updateCheckedStatus,
      openFolder,
      onClickCheckBox,
      showChoseUser,
      onDeleteItem,
      fetchOrganizations,
      debouncedFilterOrg,
      handleFilterOrg,
      isCheckedList,
      setIsCheckedList,
      setSearchResult,
      isTreeLoading,
      isCheckedLoading,
      onClickFolder,
      valueName,
      setValueName,
    }),
    [
      tree,
      orgList,
      isCheckedList,
      searchResult,
      isTreeLoading,
      isCheckedLoading,
      valueName,
    ],
  );

  return (
    <DirectoryContext.Provider value={values}>
      {children}
    </DirectoryContext.Provider>
  );
};
